/**
 * Local key estimation.
 *
 * ## Why this is not an LLM call
 *
 * The key estimate drives `respell()`, and spelling cannot wait for the network. A chord
 * has to appear on screen with its letters already chosen, tens of milliseconds after it
 * was played, on an aeroplane if need be. So the estimate is computed here: a weighted
 * pitch-class histogram over the recent path, correlated against the twenty-four
 * Krumhansl–Kessler key profiles.
 *
 * ## The method
 *
 * 1. Take the last `KEY_ESTIMATE_WINDOW` nodes. Older harmony is not evidence about where
 *    we are *now* — a piece that has modulated should follow, and an eight-chord window
 *    is roughly the memory a listener uses.
 * 2. Weight each node by recency (`KEY_ESTIMATE_RECENCY ^ distance-from-the-end`) and add
 *    a bonus for its root and bass, which carry more tonal information than an inner voice.
 * 3. Correlate the histogram against each of the 24 rotated profiles (Pearson).
 * 4. The best correlation names the key. Confidence combines *fit* (how well the winning
 *    profile explains the histogram at all) with *margin* (how much better it is than the
 *    runner-up), so three chords of planing fourths — which fit everything equally badly —
 *    report a low number instead of a confident lie.
 *
 * Everything here is pure and allocation-light; it is called after every chord.
 */

import { mod12 } from '../chord/enharmonic.js';
import type { PitchClass } from '../chord/types.js';
import { DEFAULT_KEY_ESTIMATE, type EstimateKey, type SessionNode } from './types.js';

/** How many nodes back the histogram looks. Beyond this, a modulation would never settle. */
export const KEY_ESTIMATE_WINDOW = 8;

/** Per-node geometric decay going backwards from the most recent chord. */
export const KEY_ESTIMATE_RECENCY = 0.85;

/** Extra weight the root of each chord contributes, on top of its membership. */
export const ROOT_WEIGHT = 0.75;

/** Extra weight the sounding bass contributes. Lower than the root: inversions are common. */
export const BASS_WEIGHT = 0.25;

/**
 * Confidence shaping. `confidence = clamp01(rBest) * clamp01(BASE + (rBest - rSecond) * GAIN)`.
 * A perfect fit that is only marginally better than its relative minor still reports high
 * confidence (the two share a key signature, so spelling is unaffected); a mediocre fit
 * never does, whatever the margin.
 */
export const KEY_CONFIDENCE_MARGIN_BASE = 0.5;
export const KEY_CONFIDENCE_MARGIN_GAIN = 2.5;

/**
 * How much of a scale we need to have heard before the estimate is allowed to be certain.
 * One C major triad correlates beautifully with C major — and with F major, and with G
 * major, and it is only three notes. Scaling by the distinct pitch classes seen over a
 * full diatonic collection is what stops the first chord of a session claiming to know
 * the key.
 */
export const KEY_CONFIDENCE_EVIDENCE_TARGET = 7;

/**
 * Krumhansl–Kessler major profile: the average rated stability of each scale degree,
 * indexed from the tonic.
 */
export const KRUMHANSL_MAJOR_PROFILE: readonly number[] = Object.freeze([
  6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88,
]);

/** Krumhansl–Kessler minor profile, indexed from the tonic. */
export const KRUMHANSL_MINOR_PROFILE: readonly number[] = Object.freeze([
  6.33, 2.68, 3.52, 5.38, 2.6, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17,
]);

const MODES: ReadonlyArray<'major' | 'minor'> = Object.freeze(['major', 'minor'] as const);

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/** Six decimal places is well beyond anything the UI shows, and keeps snapshots stable. */
function round6(value: number): number {
  return Math.round(value * 1e6) / 1e6;
}

/**
 * The weighted pitch-class histogram the correlation runs against.
 *
 * Exported because the tuning and diagnostics screens want to show it: "this is why the
 * app thinks you are in E♭" is a much better explanation than a bare key name.
 */
export function keyHistogram(
  path: readonly SessionNode[],
  window: number = KEY_ESTIMATE_WINDOW,
): number[] {
  const histogram = new Array<number>(12).fill(0);
  const start = Math.max(0, path.length - Math.max(1, window));
  const recent = path.slice(start);
  for (let i = 0; i < recent.length; i += 1) {
    const node = recent[i] as SessionNode;
    const weight = KEY_ESTIMATE_RECENCY ** (recent.length - 1 - i);
    for (const pc of node.chord.pitchClasses) histogram[pc] += weight;
    histogram[node.chord.rootPc] += weight * ROOT_WEIGHT;
    histogram[node.chord.bassPc] += weight * BASS_WEIGHT;
  }
  return histogram;
}

/** Pearson correlation. Returns 0 when either vector is constant, rather than NaN. */
export function correlate(a: readonly number[], b: readonly number[]): number {
  const n = Math.min(a.length, b.length);
  if (n === 0) return 0;
  let meanA = 0;
  let meanB = 0;
  for (let i = 0; i < n; i += 1) {
    meanA += a[i] as number;
    meanB += b[i] as number;
  }
  meanA /= n;
  meanB /= n;

  let covariance = 0;
  let varianceA = 0;
  let varianceB = 0;
  for (let i = 0; i < n; i += 1) {
    const da = (a[i] as number) - meanA;
    const db = (b[i] as number) - meanB;
    covariance += da * db;
    varianceA += da * da;
    varianceB += db * db;
  }
  const denominator = Math.sqrt(varianceA * varianceB);
  return denominator === 0 ? 0 : covariance / denominator;
}

/** One candidate key and how well it explains the histogram. */
export interface KeyCorrelation {
  tonicPc: PitchClass;
  mode: 'major' | 'minor';
  r: number;
}

/**
 * All 24 correlations, best first. Ties break toward the lower tonic and then toward
 * major, so the result never depends on iteration order.
 */
export function correlateAllKeys(histogram: readonly number[]): KeyCorrelation[] {
  const scored: KeyCorrelation[] = [];
  for (let tonic = 0; tonic < 12; tonic += 1) {
    for (const mode of MODES) {
      const profile = mode === 'major' ? KRUMHANSL_MAJOR_PROFILE : KRUMHANSL_MINOR_PROFILE;
      const rotated = new Array<number>(12);
      for (let pc = 0; pc < 12; pc += 1) rotated[pc] = profile[mod12(pc - tonic)] as number;
      scored.push({ tonicPc: tonic as PitchClass, mode, r: correlate(histogram, rotated) });
    }
  }
  scored.sort(
    (x, y) =>
      y.r - x.r || x.tonicPc - y.tonicPc || (x.mode === y.mode ? 0 : x.mode === 'major' ? -1 : 1),
  );
  return scored;
}

/**
 * `estimateKey(path)` — see `EstimateKey` in `types.ts`.
 *
 * An empty path is C major with zero confidence, which is what
 * `createInitialSessionState` starts from; there is nothing to be confident about yet.
 */
export const estimateKey: EstimateKey = (path) => {
  if (!path || path.length === 0) return { ...DEFAULT_KEY_ESTIMATE };

  const histogram = keyHistogram(path);
  let total = 0;
  let distinct = 0;
  for (const value of histogram) {
    total += value;
    if (value > 0) distinct += 1;
  }
  if (total <= 0) return { ...DEFAULT_KEY_ESTIMATE };

  const scored = correlateAllKeys(histogram);
  const best = scored[0] as KeyCorrelation;
  const second = scored[1] as KeyCorrelation;

  const fit = clamp01(best.r);
  const margin = clamp01(
    KEY_CONFIDENCE_MARGIN_BASE + (best.r - second.r) * KEY_CONFIDENCE_MARGIN_GAIN,
  );
  const evidence = clamp01(distinct / KEY_CONFIDENCE_EVIDENCE_TARGET);

  return {
    tonicPc: best.tonicPc,
    mode: best.mode,
    confidence: round6(fit * margin * evidence),
    secondBest: { tonicPc: second.tonicPc, mode: second.mode },
  };
};
